import { useState, useEffect } from "react";
import { Form, Transfer, Checkbox } from "antd";
import { OpCol } from "components/customAntd/DLS/OpCol/OpCol";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { OpRow } from "components/customAntd/DLS/OpRow/OpRow";
import { DATE_FORMAT } from "constants/dates";
import { useTranslation } from "react-i18next";
import { Role } from "types/roleTypes";
import { User } from "types/userTypes";
import { Location } from "types/locationTypes";
import type { TransferProps } from "antd";

interface UserFormProps {
    selectedUser: User | null;
    locations: Location[];
    roles: Role[];
    locationAccess: number[]; // Array of siteIds the user has access to
    isReadOnly: boolean;
    isSuperAdmin: boolean; // Only super admins may edit location limitation
}

const UserForm: React.FC<UserFormProps> = ({ selectedUser, roles, locations, locationAccess, isReadOnly, isSuperAdmin }) => {
    const { t } = useTranslation();
    const isMasterUser = !!selectedUser?.masterIntegrationId;
    const form = Form.useFormInstance();

    // Track location limitation mode
    const [limitLocations, setLimitLocations] = useState<boolean>(locationAccess.length > 0);
    const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>(locationAccess);

    useEffect(() => {
        // Update state when locationAccess changes
        const isLimited = locationAccess.length > 0;
        setLimitLocations(isLimited);
        setSelectedLocationIds(locationAccess);

        // Set form values
        form.setFieldsValue({
            limitLocations: isLimited,
            locationIds: locationAccess,
        });
    }, [locationAccess, form]);

    const handleLimitLocationsChange = (e: any) => {
        const checked = e.target.checked;
        setLimitLocations(checked);

        // Set both fields and mark them as touched
        form.setFieldsValue({
            limitLocations: checked,
            locationIds: checked ? (locationAccess.length > 0 ? locationAccess : []) : []
        });

        if (!checked) {
            // 'All Locations': locationIds만 비우고 defaultLocation은 유지
            setSelectedLocationIds([]);
            // Clear validation errors
            form.setFields([{ name: 'locationIds', errors: [], touched: true }]);
            form.setFields([{ name: 'limitLocations', errors: [], touched: true }]);
        } else {
            // 'Limit Locations': 기존 locationAccess를 초기값으로 설정
            const restoredLocationIds = locationAccess.length > 0 ? locationAccess : [];
            setSelectedLocationIds(restoredLocationIds);

            // If there are restored selected locations and the current default location
            // is not in that list, clear it to prevent saving an invalid default location.
            // When restoredLocationIds is empty, all locations are valid options so we keep
            // the current default location as-is.
            const currentDefaultLocation = form.getFieldValue('defaultLocationId');
            if (
                currentDefaultLocation &&
                restoredLocationIds.length > 0 &&
                !restoredLocationIds.includes(currentDefaultLocation)
            ) {
                form.setFieldsValue({ defaultLocationId: undefined });
            }

            form.setFields([{ name: 'limitLocations', errors: [], touched: true }]);
        }
    };

    const handleTransferChange: TransferProps['onChange'] = (targetKeys) => {
        const numericKeys = targetKeys.map(key => Number(key));
        setSelectedLocationIds(numericKeys);
        form.setFieldsValue({ locationIds: numericKeys });

        // Clear default location if it's not in the selected locations
        const currentDefaultLocation = form.getFieldValue('defaultLocationId');
        if (currentDefaultLocation && !numericKeys.includes(currentDefaultLocation)) {
            form.setFieldsValue({ defaultLocationId: undefined });
        }
    };

    // Prepare transfer data source
    const transferDataSource = locations.map((location: Location) => ({
        key: location.id.toString(),
        title: location.name,
    }));

    // Filter default location options
    // If limitLocations is checked but no locations are selected yet, show all locations.
    // Only filter when there is at least one selected location.
    const defaultLocationOptions = limitLocations && selectedLocationIds.length > 0
        ? locations.filter((location: Location) => selectedLocationIds.includes(location.id))
            .map((location: Location) => ({ label: location.name, value: location.id }))
        : locations.map((location: Location) => ({ label: location.name, value: location.id }));

    return (
        <>
            <OpForm.Input
                label={t('user.form.email')}
                name={['identity', 'email']}
                rules={[{ required: true, message: t('user.form.emailRequired'), type: 'email' }]}
                disabled={selectedUser ? true : false}
            />

            <OpForm.Input
                label={t('user.form.firstName')}
                name={['identity', 'firstName']}
                rules={[{ required: true, message: t('user.form.firstNameRequired'), type: 'string' }]}
                disabled={isMasterUser}
            />

            <OpForm.Input label={t('user.form.lastName')} name={['identity', 'lastName']} disabled={isMasterUser} />

            <OpForm.Input label={t('user.form.middleName')} name={['identity', 'middleName']} disabled={isMasterUser} />

            <OpForm.Item
                label={t('user.form.locationLimitation')}
                name="limitLocations"
                valuePropName="checked"
            >
                <Checkbox
                    onChange={handleLimitLocationsChange}
                    disabled={isMasterUser || isReadOnly || !isSuperAdmin}
                >
                    {t('user.form.limitToSpecificLocations')}
                </Checkbox>
            </OpForm.Item>

            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.limitLocations !== currentValues.limitLocations || prevValues.locationIds !== currentValues.locationIds}>
                {({ getFieldValue }) => {
                    const isLimited = getFieldValue('limitLocations');
                    return isLimited ? (
                        <OpForm.Item
                            label={t('user.form.selectLocations')}
                            name="locationIds"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value || value.length === 0) {
                                            return Promise.reject(new Error(t('user.form.locationRequired')));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Transfer
                                dataSource={transferDataSource}
                                targetKeys={selectedLocationIds.map(id => id.toString())}
                                onChange={handleTransferChange}
                                render={item => item.title}
                                showSearch
                                disabled={isMasterUser || isReadOnly || !isSuperAdmin}
                                style={{ width: '100%' }}
                                listStyle={{
                                    flex: 1,
                                    height: 300,
                                }}
                                titles={[t('user.form.availableLocations'), t('user.form.selectedLocations')]}
                                locale={{
                                    itemUnit: t('user.form.location'),
                                    itemsUnit: t('user.form.locations'),
                                    searchPlaceholder: t('user.form.searchLocations'),
                                }}
                            />
                        </OpForm.Item>
                    ) : null;
                }}
            </Form.Item>

            <OpForm.Select
                label={t('user.form.defaultLocation')}
                name="defaultLocationId"
                options={defaultLocationOptions}
                allowClear
                disabled={isMasterUser}
            />

            <OpForm.Input label={t('user.form.phone')} name={['identity', 'mobilePhone']} disabled={isMasterUser} />

            <OpRow gutter={16}>
                <OpCol span={12}>
                    <OpForm.DatePicker format={DATE_FORMAT} label={t('user.form.startDate')} name="startDate" disabled={isMasterUser} />
                </OpCol>
                <OpCol span={12}>
                    <OpForm.DatePicker format={DATE_FORMAT} label={t('user.form.endDate')} name="endDate" disabled={isMasterUser} />
                </OpCol>
            </OpRow>

            <OpRow gutter={16}>
                <OpCol span={12}>
                    <OpForm.Input label={t('user.form.department')} name="department" disabled={isMasterUser} />
                </OpCol>
                <OpCol span={12}>
                    <OpForm.Input label={t('user.form.title')} name="title" disabled={isMasterUser} />
                </OpCol>
            </OpRow>

            <OpRow gutter={16}>
                <OpCol span={12}>
                    <OpForm.Input label={t('user.form.personId')} name="personId" disabled={isMasterUser} />
                </OpCol>
                <OpCol span={12}>
                    <OpForm.Input label={t('user.form.externalId')} name="externalId" disabled={isMasterUser} />
                </OpCol>
            </OpRow>

            <OpForm.Select
                label={t('user.form.roles')} name="roleId"
                rules={[{ required: true, message: t('user.form.rolesRequired') }]}
                mode="multiple"
                placeholder={t('user.form.rolesPlaceholder')}
                options={roles.map((role: Role) => ({ label: role.name, value: role.id }))}
            />
        </>
    );
};

export default UserForm;
